import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
// GMA-1228: `moment-timezone` (not plain `moment`) so we can construct and
// format activity instants anchored in the selected client's IANA zone. The
// package is already a runtime dependency (`moment-timezone ^0.6.0`) so this
// swap does not add a new import cost.
import moment from "moment-timezone";
import { ClientContext } from "./ClientContext";
import { apiRequest, apiRequestFormData } from "../api/baseApi";
import { wasAuthFailureReported } from "../api/authFailureMarker";
import { addBreadcrumb, captureMessage } from "../config/sentry";
import { activityInstantToEventCalendarDate } from "../utils/eventCalendarDates";
import {
  ACTIVITY_STATES,
  resolveActivityCompletionState,
} from "../utils/activityCompletionState";
import {
  buildZonedInstant,
  guessCaregiverZone,
  MESSAGE_ACTIVITY_NO_PAST_START,
  MESSAGE_ACTIVITY_NO_PAST_START_TIME,
  reportTimezoneMismatchIfAny,
  resolveActivityTimezone,
  toWallClockHHmm,
} from "../utils/activityTimezone";

/** Returns true only for values safe to PATCH as stored S3 keys (never presigned URLs). */
const isStoredStepImageKeyCandidate = (v) =>
  typeof v === "string" &&
  v.trim().length > 0 &&
  !/^https?:\/\//i.test(v) &&
  !v.includes("X-Amz-Algorithm") &&
  !v.includes("X-Amz-Signature");

/**
 * GMA-1234: All-day rows store their span as client-zone midnight on date_from
 * to client-zone midnight on the day AFTER date_to (FullCalendar exclusive-end
 * convention — same shape `@event-calendar/core` emits when allDay rows are
 * selected on the grid). The HH:mm wall fields on the form are intentionally
 * ignored on this path; date_from / date_to are the only inputs that matter.
 *
 * Single-day all-day:    date_from=2026-05-06, date_to=2026-05-06
 *   → start = 2026-05-06T00:00 client zone
 *   → end   = 2026-05-07T00:00 client zone
 * Multi-day all-day:     date_from=2026-05-06, date_to=2026-05-08
 *   → start = 2026-05-06T00:00 client zone
 *   → end   = 2026-05-09T00:00 client zone
 *
 * GMA-1234 (review feedback): route the raw `clientZone` through
 * `resolveActivityTimezone` so a missing or invalid zone emits the same
 * `activity_timezone_resolve` / `activity_timezone_fallback` Sentry
 * breadcrumb the timed write path emits via `buildZonedInstant`. Without
 * this, an all-day save on a client whose `timezone` field is null silently
 * resolved to whatever raw string was passed (or threw when validated by
 * moment-tz), with no breadcrumb to correlate the resulting wrong-zone
 * instants. `options.source` keys the breadcrumb (`create-all-day` /
 * `update-all-day`) so we can tell timed and all-day fallbacks apart.
 */
const isValidYmdString = (v) =>
  typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v.trim());

const buildAllDayInstants = (dateFrom, dateTo, clientZone, options = {}) => {
  // PR #139 re-review (Copilot): validate inputs before constructing
  // moments. If `dateFrom` / `dateTo` is missing, blank, or not a valid
  // YYYY-MM-DD, moment.tz returns an invalid moment whose .toISOString()
  // is null, and we used to flow that null through to the POST/PATCH body
  // — silently writing a broken row. Reject up front with a user-facing
  // message that mirrors the timed path's "Select start date and time."
  // copy so caregivers see a sensible toast instead of a 500.
  if (!isValidYmdString(dateFrom)) {
    throw new Error("Select start date.");
  }
  if (!isValidYmdString(dateTo)) {
    throw new Error("Select end date.");
  }
  const { zone } = resolveActivityTimezone(clientZone, {
    source: options.source || "all-day-write",
    clientId: options.clientId,
  });
  const startDateTime = moment
    .tz(dateFrom.trim(), "YYYY-MM-DD", zone)
    .startOf("day");
  const endDateTime = moment
    .tz(dateTo.trim(), "YYYY-MM-DD", zone)
    .startOf("day")
    .add(1, "day");
  // Belt-and-braces: reject impossible dates that pass the regex but
  // aren't real (e.g. "2026-02-30"). moment.tz with strict format would
  // be cleaner but the call site already used non-strict; this catches
  // the same cases without changing behaviour for valid inputs.
  if (!startDateTime.isValid()) {
    throw new Error("Select a valid start date.");
  }
  if (!endDateTime.isValid()) {
    throw new Error("Select a valid end date.");
  }
  return { startDateTime, endDateTime, zone };
};

/**
 * GMA-1234 (review feedback for GMA-1228 regression): an all-day activity
 * with `repeat_type !== "none"` and a multi-day `date_to` would send a single
 * occurrence whose end is `(date_to + 1 day) 00:00` and whose start is
 * `date_from 00:00` — i.e. > 24h. That is exactly the stretched-span shape
 * GMA-1228 fixed for timed activities (the form bug that flooded the
 * calendar with year-long recurrences). The timed normalizer
 * (`normalizeRecurringSpan`) requires an HH:mm `timeTo` to coerce against,
 * which the all-day path does not have, so we mirror the same behaviour with
 * a date-only coercion: clamp `endDateTime` back to `startDateTime + 1 day`
 * for recurring all-day rows and emit a breadcrumb so we can watch the tail
 * settle on stg2 / prod the same way.
 */
const normalizeRecurringAllDaySpan = ({
  startDateTime,
  endDateTime,
  repeatType,
  breadcrumbEvent,
  breadcrumbExtra = {},
}) => {
  const isRecurring = repeatType && repeatType !== "none";
  // PR #139 re-review (Copilot): use a calendar-day comparison instead of
  // an absolute-hours threshold. On DST fall-back the wall midnight to
  // wall midnight span is 25h (an extra hour at 02:00 -> 01:00), so a
  // legitimate single-day all-day in America/Denver on the November DST
  // boundary would have falsely tripped the > 24h coercion path and
  // emitted a noisy `suspicious_span` breadcrumb to Sentry even though
  // the coerced end would have been identical to the original. Comparing
  // against `start + 1 day` (which moment computes in calendar-day
  // arithmetic, honouring the local zone's DST table) keeps the coercion
  // firing only when caregivers actually picked a multi-day `date_to` on
  // a recurring all-day, regardless of DST.
  const oneDayEnd = startDateTime.clone().add(1, "day");
  if (!isRecurring || !endDateTime.isAfter(oneDayEnd)) {
    return { startDateTime, endDateTime, isRecurring, coerced: false };
  }
  const coercedEnd = oneDayEnd;
  addBreadcrumb(breadcrumbEvent, {
    category: breadcrumbEvent.split(".").slice(0, 2).join("."),
    level: "warning",
    data: {
      ticket: "GMA-1234",
      mode: "all-day",
      originalEndTime: endDateTime.toISOString(),
      coercedEndTime: coercedEnd.toISOString(),
      repeatType,
      ...breadcrumbExtra,
    },
  });
  return {
    startDateTime,
    endDateTime: coercedEnd,
    isRecurring: true,
    coerced: true,
  };
};

const normalizeRepeatDays = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (raw === null || raw === undefined || raw === "") return [];
  if (typeof raw === "number" && Number.isInteger(raw)) return [raw];
  if (typeof raw === "string") {
    const parts = raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .map(Number);
    if (parts.every((n) => Number.isInteger(n))) return parts;
  }
  return [];
};

/**
 * GMA-1228: Normalize the recurrence payload for both `createActivity` and
 * `updateActivity`.
 *
 * On recurring activities (`repeat_type !== "none"`):
 *   - The single-occurrence duration must not exceed 24 hours. Before the
 *     fix, the form allowed `date_to` to stretch the occurrence across the
 *     whole recurrence window, producing a single seed row that backed every
 *     rendered day of the series (the calendar flood + the "Delete Occurrence
 *     wipes series" downstream symptom).
 *   - Emit a `activity.{create|update}.suspicious_span` Sentry breadcrumb
 *     on each coercion so we can watch the stretch-span tail settle on
 *     stg2 / prod after the form fix ships.
 *
 * On non-recurring activities, spans are preserved verbatim — legitimate
 * multi-day activities (e.g. overnight stays) must not be clipped.
 *
 * Returns `{ startDateTime, endDateTime, isRecurring, coerced }`.
 */
const normalizeRecurringSpan = ({
  startDateTime,
  endDateTime,
  timeTo,
  repeatType,
  breadcrumbEvent,
  breadcrumbExtra = {},
}) => {
  const isRecurring = repeatType && repeatType !== "none";
  if (
    !isRecurring ||
    endDateTime.diff(startDateTime, "hours", true) <= 24
  ) {
    return { startDateTime, endDateTime, isRecurring, coerced: false };
  }
  const hhmm = toWallClockHHmm(timeTo);
  const time = hhmm ? moment(hhmm, ["HH:mm", "HH:mm:ss"], true) : moment.invalid();
  if (!time.isValid()) {
    return { startDateTime, endDateTime, isRecurring, coerced: false };
  }
  const coercedEnd = moment(startDateTime).set({
    hour: time.hour(),
    minute: time.minute(),
    second: 0,
    millisecond: 0,
  });
  addBreadcrumb(breadcrumbEvent, {
    category: breadcrumbEvent.split(".").slice(0, 2).join("."),
    level: "warning",
    data: {
      ticket: "GMA-1228",
      originalEndTime: endDateTime.toISOString(),
      coercedEndTime: coercedEnd.toISOString(),
      repeatType,
      ...breadcrumbExtra,
    },
  });
  return {
    startDateTime,
    endDateTime: coercedEnd,
    isRecurring: true,
    coerced: true,
  };
};

/**
 * GMA-1228: Apply recurrence fields (`repeat_end_date`, `repeat_days`) to an
 * outgoing request body only when the activity is actually recurring. Before
 * the fix, `repeat_end_date` was sent even for one-off activities (seeded
 * from the hidden +365d default), which muddled the `Activity.create` audit
 * trail on the backend.
 */
const applyRecurrenceFields = (requestBody, data, isRecurring) => {
  if (!isRecurring) return requestBody;
  if (data.repeat_end_date) {
    requestBody.repeat_end_date = data.repeat_end_date;
  }
  requestBody.repeat_days = normalizeRepeatDays(data.repeat_days);
  return requestBody;
};

/**
 * Belt-and-braces guard: block saves that imply a wall-clock start in the past on
 * the client's calendar even if Yup/Formik is bypassed (GMA-176).
 *
 * GMA-1234 (review feedback): all-day rows pin start to client-zone midnight,
 * which is always strictly before "now" once any time has elapsed today.
 * Dropping this guard entirely on the all-day path (the original GMA-1234
 * commit) regressed the past-day half of GMA-176, so calendar UI bypasses
 * could once again create activities on yesterday or earlier. The fix is
 * to keep the past-DAY check on for all-day saves and only opt out of the
 * past-time-on-today check, by passing `allowPastTimeOnToday: true`.
 */
const enforcePastStartForbidden = ({
  startDateTime,
  clientZone,
  clientId,
  actionTag,
  allowPastTimeOnToday = false,
}) => {
  const { zone } = resolveActivityTimezone(clientZone, {
    source: `${actionTag}-past-guard`,
    clientId,
  });
  if (
    startDateTime === null ||
    startDateTime === undefined ||
    typeof startDateTime.isValid !== "function" ||
    !startDateTime.isValid()
  ) {
    throw new Error("Invalid activity start date or time.");
  }
  const wallStart = startDateTime.clone().tz(zone);
  const nowZ = moment.tz(zone);
  const startDay = wallStart.clone().startOf("day");
  const todayDay = nowZ.clone().startOf("day");

  const capturePastStartBlocked = (reason) =>
    captureMessage("[GMA-176] activity write blocked past start", {
      level: "info",
      tags: {
        ticket: "GMA-176",
        action: actionTag || "unknown",
        reason,
      },
      extra: { clientId: clientId ?? null },
    });

  if (startDay.isBefore(todayDay)) {
    capturePastStartBlocked("past-calendar-day");
    throw new Error(MESSAGE_ACTIVITY_NO_PAST_START);
  }
  if (
    !allowPastTimeOnToday &&
    startDay.isSame(todayDay, "day") &&
    wallStart.isBefore(nowZ)
  ) {
    capturePastStartBlocked("past-time-on-today");
    throw new Error(MESSAGE_ACTIVITY_NO_PAST_START_TIME);
  }
};

export const ActivityContext = createContext();

export const ActivityProvider = ({ children }) => {
  const { selectedClient } = useContext(ClientContext);
  const [activities, setActivities] = useState([]);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activitiesError, setActivitiesError] = useState(null);
  // #618: mount starts an unbounded load while `datesSet` schedules a bounded
  // one, so two are in flight routinely. Without a generation the slower one
  // wins whenever it resolves last, silently replacing the visible range's rows
  // — or raising an error over a load that already succeeded.
  const activitiesGenerationRef = useRef(0);
  // #635 review: a generation orders requests but does not BIND one to a
  // client. A getActivities captured under client A (an awaited refresh
  // inside create/update/delete) can start after the switch to B, take the
  // NEWEST generation, and overwrite B's schedule with A's rows — so every
  // request also carries the client id it was created for, checked against
  // the committed one before fetching and before every write.
  const committedClientIdRef = useRef(null);

  const getActivities = useCallback(
    async (start, end) => {
      const myClientId = selectedClient?.id ?? null;
      if (committedClientIdRef.current !== myClientId) {
        // Captured under a client that is no longer committed — fetching
        // would waste a request whose result may never be applied.
        return [];
      }
      const myGeneration = ++activitiesGenerationRef.current;
      const isStale = () =>
        activitiesGenerationRef.current !== myGeneration ||
        committedClientIdRef.current !== myClientId;
      setIsLoading(true);
      try {
        if (!selectedClient) return [];
        let url = `clients/${selectedClient.id}/activities/`;
        const params = [];
        if (start) params.push(`start=${encodeURIComponent(start)}`);
        if (end) params.push(`end=${encodeURIComponent(end)}`);
        if (params.length) {
          url += `?${params.join("&")}`;
        }
        const response = await apiRequest(url, {
          method: "GET",
          version: "v3",
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          // `?.` because a body of JSON `null` RESOLVES — the catch above only
          // covers a body that fails to parse. Without it this known HTTP
          // response threw a statusless TypeError and read as `network`.
          const httpError = new Error(
            errorData?.message || "Failed to fetch activities"
          );
          httpError.status = response.status;
          // #673: this path does not go through parseJsonOrThrow. A failed
          // reactive refresh stamps the returned 401 via authFailureMarker;
          // copy that stamp so classification below records `auth`, matching
          // the proactive token-acquisition path, instead of `http`.
          if (wasAuthFailureReported(response)) {
            httpError.isAuthFailure = true;
          }
          throw httpError;
        }

        // A 2xx whose body will not parse is NOT a network failure: the HTTP
        // request succeeded. Classifying it as `network` sends whoever reads
        // the breadcrumb to look at connectivity for a server that answered.
        let data;
        try {
          data = await response.json();
        } catch (parseError) {
          parseError.status = response.status;
          parseError.isParseFailure = true;
          throw parseError;
        }
        if (isStale()) return data || [];
        setActivities(data || []);
        setActivitiesError(null);
        addBreadcrumb("calendar.activities.load.succeeded", {
          category: "calendar.activities.load.succeeded",
          level: "info",
          count: (data || []).length,
        });
        return data || [];
      } catch (error) {
        // #618: this path used to have no catch at all, so every failure left
        // the caregiver an empty grid that reads as "this client has no
        // activities" and surfaced only as an unhandled rejection.
        //
        // Swallowing is safe ONLY because #633 made apiRequest report non-2xx
        // and transport failures centrally. Before that, catching here would
        // have removed the last signal this outage class produced.
        const status =
          typeof error?.status === "number" ? error.status : undefined;
        const detail = error?.isParseFailure
          ? { reason: "parse", status }
          : error?.isAuthFailure
            ? // apiRequest marks token-acquisition failures and reports them
              // itself (api.request.token_failed) — recording `network` here
              // would contradict that diagnosis.
              { reason: "auth" }
            : status
              ? { reason: "http", status }
              : { reason: "network" };

        if (error?.isParseFailure) {
          // apiRequest reports transport and non-2xx failures centrally, but a
          // 2xx whose body will not parse never reaches it, and a breadcrumb
          // alone sends no event — swallowing without this would delete the
          // class's only Sentry signal (it used to arrive as an unhandled
          // rejection). Before the staleness guard: a malformed body is worth
          // knowing about even when its request lost the race.
          captureMessage("[#618] calendar activities load: 2xx body failed to parse", {
            level: "error",
            tags: { status },
          });
        }

        if (isStale()) return undefined;
        setActivitiesError(detail);
        addBreadcrumb("calendar.activities.load.failed", {
          category: "calendar.activities.load.failed",
          level: "error",
          ...detail,
        });

        // Deliberately NOT clearing `activities`: stale rows are honest about
        // being stale once the error state renders, while a blank grid is a
        // confident lie. No client id or name goes into telemetry.
        return undefined;
      } finally {
        if (!isStale()) setIsLoading(false);
      }
    },
    [selectedClient]
  );

  // The provider stays mounted across /calendar/:clientId param changes, so
  // "keep stale rows on failure" is scoped to the SAME client: switching from
  // client A to client B must never leave A's schedule rendered under B's
  // name, even for one frame or when B's load fails. This is React's
  // derive-state-during-render pattern: setting state HERE re-renders the
  // provider before children render, so no child (and no child effect feeding
  // an imperative calendar) ever observes A's rows under B — an effect-based
  // clear leaves exactly that one committed frame. Keyed on the id, not object
  // identity: a context value rebuilt for the same client must not blank rows.
  const [renderedClientId, setRenderedClientId] = useState(null);
  const currentClientId = selectedClient?.id ?? null;
  useLayoutEffect(() => {
    // Invalidate the PREVIOUS client's in-flight request at commit, not
    // during render and not in a passive effect. During render is unsafe:
    // the router wraps navigations in startTransition (App.jsx
    // v7_startTransition), so a switch render can be discarded after the
    // increment, stranding the still-committed client's request as stale —
    // it would skip its result AND its setIsLoading(false). A passive effect
    // is too late: the old request could settle in the paint gap. A layout
    // effect runs synchronously inside the commit task, before any microtask
    // can deliver a settled promise and before the passive effect that
    // starts the next load.
    activitiesGenerationRef.current += 1;
    committedClientIdRef.current = currentClientId;
  }, [currentClientId]);
  if (renderedClientId !== currentClientId) {
    setRenderedClientId(currentClientId);
    if (renderedClientId !== null) {
      setActivities([]);
      setActivitiesError(null);
    }
    // Loading is decided by what comes NEXT, not by the old request (whose
    // finally deliberately skips setIsLoading — it must not stomp a newer
    // one). A newly selected client is loading from this very frame, or the
    // empty-state guard commits "No activities yet" before its first request
    // begins; a deselection has no next load, so it resets to false rather
    // than stranding the spinner.
    setIsLoading(currentClientId !== null);
  }

  useEffect(() => {
    if (selectedClient) {
      getActivities();
    }
  }, [selectedClient]);

  const createActivity = async (data) => {
    setIsLoading(true);
    try {
      const isAllDay = data.is_all_day === true;
      const clientZone = selectedClient?.timezone;
      const clientId = selectedClient?.id;

      // GMA-1234: all-day rows ignore the HH:mm wall fields entirely. The
      // span is synthesised from date_from / date_to and the past-start /
      // recurrence-span heuristics below are skipped (they assume a timed
      // span and would reject midnight-on-today as "in the past").
      let startDateTime;
      let endDateTime;
      let isRecurring = data.repeat_type && data.repeat_type !== "none";

      if (isAllDay) {
        ({ startDateTime, endDateTime } = buildAllDayInstants(
          data.date_from,
          data.date_to,
          clientZone,
          { source: "create-all-day", clientId },
        ));
        reportTimezoneMismatchIfAny({
          caregiverZone: guessCaregiverZone(),
          clientZone,
          action: "create",
          clientId,
        });
        const normalized = normalizeRecurringAllDaySpan({
          startDateTime,
          endDateTime,
          repeatType: data.repeat_type,
          breadcrumbEvent: "activity.create.suspicious_span",
        });
        startDateTime = normalized.startDateTime;
        endDateTime = normalized.endDateTime;
        isRecurring = normalized.isRecurring;

        // GMA-1234 + GMA-176: keep the past-DAY half of the guard on for
        // all-day saves (yesterday / earlier still blocked) but allow the
        // pinned-to-midnight start to clear the past-time-on-today check
        // so caregivers can create an all-day row for today after noon.
        enforcePastStartForbidden({
          startDateTime,
          clientZone,
          clientId,
          actionTag: "create",
          allowPastTimeOnToday: true,
        });
      } else {
        const timeFrom = toWallClockHHmm(data.start_time);
        const timeToParsed = toWallClockHHmm(data.end_time);

        if (!timeFrom) {
          throw new Error("Select start date and time.");
        }
        if (!timeToParsed) {
          throw new Error("Select end date and time.");
        }
        // GMA-1228: anchor the wall-clock `YYYY-MM-DD HH:mm` payload in the
        // client's IANA zone before serialising. Plain `moment(\`${date} ${time}\`)`
        // parsed in the caregiver browser zone, which produced a ±N hour skew
        // when caregiver and client lived in different zones (QA: 7:03 AM
        // saved, 2:03 AM rendered for a Chicago client).
        const startDateTimeRaw = buildZonedInstant(
          data.date_from,
          timeFrom,
          clientZone,
          { source: "create", clientId },
        );
        const endDateTimeRaw = buildZonedInstant(
          data.date_to,
          timeToParsed,
          clientZone,
          { source: "create", clientId },
        );
        reportTimezoneMismatchIfAny({
          caregiverZone: guessCaregiverZone(),
          clientZone,
          action: "create",
          clientId,
        });
        const normalized = normalizeRecurringSpan({
          startDateTime: startDateTimeRaw,
          endDateTime: endDateTimeRaw,
          timeTo: timeToParsed,
          repeatType: data.repeat_type,
          breadcrumbEvent: "activity.create.suspicious_span",
        });
        startDateTime = normalized.startDateTime;
        endDateTime = normalized.endDateTime;
        isRecurring = normalized.isRecurring;

        enforcePastStartForbidden({
          startDateTime,
          clientZone,
          clientId,
          actionTag: "create",
        });
      }

      const stepsPayload = (data.steps || []).map(({ step_image: _img, ...rest }) => rest);

      const requestBody = applyRecurrenceFields(
        {
          user_id: selectedClient?.id,
          title: data.title,
          description: data.description,
          start_time: startDateTime.toISOString(),
          end_time: endDateTime.toISOString(),
          is_all_day: isAllDay,
          reminder_enabled: data.reminder_enabled || false,
          is_important: data.is_important || false,
          repeat_type: data.repeat_type,
          video_url: data.video_url || null,
          steps: stepsPayload,
        },
        data,
        isRecurring,
      );

      const response = await apiRequest("activities", {
        method: "POST",
        body: requestBody,
        version: "v3",
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to create activity");
      }

      const result = await response.json();

      if (data.activity_image instanceof File) {
        try {
          await uploadActivityImage(result.id, data.activity_image);
        } catch (imgErr) {
          console.error("[ActivityContext] Failed to upload activity image:", imgErr.message);
        }
      }

      if (result.steps && data.steps) {
        for (let i = 0; i < data.steps.length; i++) {
          const stepImage = data.steps[i].step_image;
          const resultStep = result.steps.find((s) => s.sequence === data.steps[i].sequence);
          if (stepImage instanceof File && resultStep?.id) {
            try {
              await uploadStepImage(resultStep.id, stepImage);
            } catch (imgErr) {
              console.error(`[ActivityContext] Failed to upload step image (seq ${data.steps[i].sequence}):`, imgErr.message);
            }
          }
        }
      }

      await getActivities();
      setSelectedActivity(null);
    } finally {
      setIsLoading(false);
    }
  };

  const updateActivity = async (activityId, data) => {
    setIsLoading(true);
    try {
      const isAllDay = data.is_all_day === true;
      const clientZone = selectedClient?.timezone;
      const clientId = selectedClient?.id;

      // GMA-1234: see `createActivity` — all-day path bypasses the timed
      // start/end heuristics and synthesises midnight ↔ next-day midnight
      // ISO instants in the client zone.
      let startDateTime;
      let endDateTime;

      if (isAllDay) {
        ({ startDateTime, endDateTime } = buildAllDayInstants(
          data.date_from,
          data.date_to,
          clientZone,
          { source: "update-all-day", clientId },
        ));
        reportTimezoneMismatchIfAny({
          caregiverZone: guessCaregiverZone(),
          clientZone,
          action: "update",
          clientId,
          activityId,
        });
        const normalized = normalizeRecurringAllDaySpan({
          startDateTime,
          endDateTime,
          repeatType: data.repeat_type,
          breadcrumbEvent: "activity.update.suspicious_span",
          breadcrumbExtra: { activityId },
        });
        startDateTime = normalized.startDateTime;
        endDateTime = normalized.endDateTime;

        // GMA-1234 + GMA-176: same allow-past-time-on-today carve-out as
        // createActivity. Past calendar days still rejected.
        enforcePastStartForbidden({
          startDateTime,
          clientZone,
          clientId,
          actionTag: "update",
          allowPastTimeOnToday: true,
        });
      } else {
        const timeFrom = toWallClockHHmm(data.start_time);
        const timeToParsed = toWallClockHHmm(data.end_time);

        if (!timeFrom) {
          throw new Error("Select start date and time.");
        }
        if (!timeToParsed) {
          throw new Error("Select end date and time.");
        }
        // GMA-1228: same client-zone anchoring as `createActivity`. Edit-path
        // PATCHes used to round-trip the skew (caregiver loads edit, the form
        // showed browser-local HH:mm, save re-serialised the skewed instant).
        const startDateTimeRaw = buildZonedInstant(
          data.date_from,
          timeFrom,
          clientZone,
          { source: "update", clientId },
        );
        const endDateTimeRaw = buildZonedInstant(
          data.date_to,
          timeToParsed,
          clientZone,
          { source: "update", clientId },
        );
        reportTimezoneMismatchIfAny({
          caregiverZone: guessCaregiverZone(),
          clientZone,
          action: "update",
          clientId,
          activityId,
        });
        const normalized = normalizeRecurringSpan({
          startDateTime: startDateTimeRaw,
          endDateTime: endDateTimeRaw,
          timeTo: timeToParsed,
          repeatType: data.repeat_type,
          breadcrumbEvent: "activity.update.suspicious_span",
          breadcrumbExtra: { activityId },
        });
        startDateTime = normalized.startDateTime;
        endDateTime = normalized.endDateTime;

        enforcePastStartForbidden({
          startDateTime,
          clientZone,
          clientId,
          actionTag: "update",
        });
      }

      const STEP_FIELDS = [
        "id",
        "title",
        "content",
        "sequence",
        "step_state",
        "video_url",
        "step_image",
      ];
      const stepsPayload = (data.steps || []).map((step) => {
        const entry = {};
        for (const k of STEP_FIELDS) {
          const v = step[k];
          if (v === undefined) continue;
          // step_image is either an S3 key string (preserve on edit) or a File blob
          // (uploaded separately via /api/v3/steps/:stepId/image). Never forward a
          // File in the JSON PATCH body (GMA-1059 regression guard).
          if (k === "step_image") {
            // GET/PATCH responses enrich step_image to presigned URLs — never
            // PUT those back as DB keys (corrupts rows; Add Image shows raw URLs).
            if (v === null) {
              entry[k] = null;
              continue;
            }
            if (typeof v !== "string") continue;
            if (!isStoredStepImageKeyCandidate(v)) continue;
          }
          entry[k] = v;
        }
        return entry;
      });

      const isRecurring = data.repeat_type && data.repeat_type !== "none";

      const requestBody = applyRecurrenceFields(
        {
          title: data.title,
          description: data.description,
          start_time: startDateTime.toISOString(),
          end_time: endDateTime.toISOString(),
          is_all_day: isAllDay,
          reminder_enabled: data.reminder_enabled || false,
          is_important: data.is_important || false,
          repeat_type: data.repeat_type,
          video_url: data.video_url || null,
          steps: stepsPayload,
        },
        data,
        isRecurring,
      );

      addBreadcrumb("activity.update.start", {
        category: "activity.update",
        level: "info",
        activityId,
        repeatType: requestBody.repeat_type,
        repeatDaysLen: Array.isArray(requestBody.repeat_days)
          ? requestBody.repeat_days.length
          : undefined,
        stepCount: stepsPayload.length,
      });

      const response = await apiRequest(`activities/${activityId}`, {
        method: "PATCH",
        body: requestBody,
        version: "v3",
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        captureMessage("[GMA-1059] activity update failed", {
          level: "error",
          tags: { ticket: "GMA-1059", action: "update-failed" },
          extra: {
            activityId,
            status: response.status,
            code: errorData?.code,
            message: errorData?.message || errorData?.error,
          },
        });
        throw new Error(errorData.message || errorData.error || "Failed to update activity");
      }

      const result = await response.json();

      if (data.activity_image instanceof File) {
        try {
          await uploadActivityImage(activityId, data.activity_image);
        } catch (imgErr) {
          console.error("[ActivityContext] Failed to upload activity image:", imgErr.message);
        }
      }

      if (data.steps) {
        for (const step of data.steps) {
          if (step.step_image instanceof File) {
            const targetStepId = step.id || result.steps?.find((s) => s.sequence === step.sequence)?.id;
            if (targetStepId) {
              try {
                await uploadStepImage(targetStepId, step.step_image);
              } catch (imgErr) {
                console.error(`[ActivityContext] Failed to upload step image (id ${targetStepId}):`, imgErr.message);
              }
            }
          }
        }
      }

      await getActivities();
      setSelectedActivity(null);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteActivity = async (deleteType = "single") => {
    if (!selectedActivity?.id) {
      throw new Error("No activity selected to delete");
    }
    setIsLoading(true);
    try {
      const response = await apiRequest(
        `activities/${selectedActivity.id}${
          deleteType === "all" ? "?deleteAll=true" : ""
        }`,
        {
          method: "DELETE",
          version: "v3",
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete activity");
      }

      await getActivities();
      setSelectedActivity(null);
    } finally {
      setIsLoading(false);
    }
  };

  const uploadActivityImage = async (activityId, imageFile) => {
    if (!imageFile) return;

    const formData = new FormData();
    formData.append("file", imageFile);

    const response = await apiRequestFormData(
      `activities/${activityId}/image`,
      {
        method: "POST",
        body: formData,
        version: "v3",
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to upload activity image");
    }

    return response.json();
  };

  const deleteActivityImage = async () => {
    const activityId = selectedActivity?.id;
    if (!activityId) throw new Error("No activity selected to delete image");
    setIsLoading(true);
    try {
      const response = await apiRequest(`activities/${activityId}/image`, {
        method: "DELETE",
        version: "v3",
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete activity image");
      }

      setActivities((prev) =>
        prev.map((a) =>
          a.id === activityId ? { ...a, activity_image: null } : a
        )
      );
      if (selectedActivity?.id === activityId) {
        setSelectedActivity((prev) =>
          prev ? { ...prev, activity_image: null } : prev
        );
      }
      return true;
    } finally {
      setIsLoading(false);
    }
  };

  const uploadStepImage = async (stepId, imageFile) => {
    if (!imageFile) return;

    const formData = new FormData();
    formData.append("file", imageFile);
    const response = await apiRequestFormData(`steps/${stepId}/image`, {
      method: "POST",
      body: formData,
      version: "v3",
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to upload step image");
    }

    return response.json();
  };

  const deleteStepImage = async (stepId) => {
    if (!stepId) throw new Error("No step ID provided");

    setIsLoading(true);
    try {
      const response = await apiRequest(`steps/${stepId}/image`, {
        method: "DELETE",
        version: "v3",
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete step image");
      }

      return true;
    } finally {
      setIsLoading(false);
    }
  };

  const selectActivity = useCallback(
    (activityId) => {
      const activity = activities.find((a) => a.id === parseInt(activityId, 10));
      setSelectedActivity(activity || null);
    },
    [activities]
  );

  const clearSelectedActivity = useCallback(() => {
    setSelectedActivity(null);
  }, []);

  const getActivityStatus = useCallback((activity) => {
    // GMA-1228-OK: `moment(activity.start_time)` below is an absolute-instant
    // comparison against "now" (zone-invariant). No wall-time formatting here.
    const startDateTime = moment(activity.start_time); // GMA-1228-OK: abs-instant
    const hasStarted = startDateTime.isSameOrBefore(moment());
    const completionState = resolveActivityCompletionState(activity);
    const fiveMinutesAfterStart = moment(startDateTime).add(5, "minutes");

    const status = !hasStarted
      ? "incomplete"
      : completionState === ACTIVITY_STATES.COMPLETE
      ? "completed"
      : moment().isAfter(fiveMinutesAfterStart)
      ? "incomplete"
      : "in_progress";

    return [hasStarted, status];
  }, []);

  const formatActivities = useCallback(
    (list) => {
      if (!list) return [];
      const rawTz = selectedClient?.timezone;
      const clientId = selectedClient?.id ?? null;
      const { zone: batchWallZone } = resolveActivityTimezone(rawTz, {
        source: "grid-batch",
        clientId,
      });
      return list.map((activity) => ({
        ...activity,
        start: activityInstantToEventCalendarDate(activity.start_time, rawTz, {
          clientId,
          source: "grid",
          resolvedWallZone: batchWallZone,
        }),
        end: activityInstantToEventCalendarDate(activity.end_time, rawTz, {
          clientId,
          source: "grid",
          resolvedWallZone: batchWallZone,
        }),
        // GMA-1234: route rows the BE marked as all-day to the calendar
        // library's all-day lane (header band above the time grid). Falsy
        // / missing flag preserves the legacy timed-row rendering exactly.
        allDay: activity.is_all_day === true,
        extendedProps: {
          event_state: activity.event_state,
          // Keep API ISO strings for tooltips + paintEvent (real instants vs "now").
          start_time: activity.start_time,
          end_time: activity.end_time,
          client_marked_not_done_at: activity.client_marked_not_done_at ?? null,
          is_important: activity.is_important,
          is_all_day: activity.is_all_day === true,
          series_id: activity.series_id,
          title: activity.title,
        },
      }));
    },
    [selectedClient?.timezone, selectedClient?.id],
  );

  const value = useMemo(
    () => ({
      activities,
      activitiesError,
      selectedActivity,
      isLoading,
      getActivities,
      createActivity,
      updateActivity,
      deleteActivity,
      uploadActivityImage,
      deleteActivityImage,
      uploadStepImage,
      deleteStepImage,
      selectActivity,
      clearSelectedActivity,
      getActivityStatus,
      formatActivities,
    }),
    [
      activities,
      activitiesError,
      selectedActivity,
      isLoading,
      getActivities,
      selectActivity,
      clearSelectedActivity,
      getActivityStatus,
      formatActivities,
    ]
  );

  return (
    <ActivityContext.Provider value={value}>
      {children}
    </ActivityContext.Provider>
  );
};

export const useActivities = () => {
  const context = useContext(ActivityContext);
  if (!context) {
    throw new Error("useActivities must be used within ActivityProvider");
  }
  return context;
};
